import { useState } from 'react';
import { Filter, X, ChevronDown, Hash, Boxes } from 'lucide-react';
import { UserSelector } from './UserSelector';
import { DriverSelector } from './DriverSelector';
import { ProductSelector } from './ProductSelector';
import { ReturnIdPicker } from './ReturnIdPicker';

const STATUS_OPTIONS = [
  { value: '',            label: 'Todos los estados de paquete' },
  { value: 'in_warehouse', label: 'En Bodega' },
  { value: 'in_fastmax',   label: 'En FastMax' },
  { value: 'with_driver',  label: 'Con Conductor' },
  { value: 'delivered',    label: 'Entregado' },
  { value: 'returned',     label: 'Devuelto' },
];

const DELIVERY_STATUS_OPTIONS = [
  { value: '',           label: 'Todos los estados de entrega' },
  { value: 'entregado',  label: 'Entregado' },
  { value: 'cancelado',  label: 'Cancelado' },
  { value: 'rechazado',  label: 'Rechazado' },
  { value: 'pending',    label: 'Pendiente' },
  { value: 'encamino',   label: 'En Camino' },
  { value: 'postergado', label: 'Postergado' },
];

/**
 * PackageFilters
 *
 * Filter bar for the package table.
 * Calls applyFilter() from the shared hook on each change.
 * Shows an "active filters" count badge when filters are applied.
 */
export function PackageFilters({ filters, applyFilter, clearFilters, users, usersLoading, activeUsersOnly, setActiveUsersOnly, requestUsers, onUserSearch, drivers, driversLoading, driversError, requestDrivers, productCounts, productCountsLoading, onOpenProductCounts, returnDraft = [], onAddReturnPackage, onRemoveReturnPackage, onPrepareReturnDraft }) {
  const [expanded, setExpanded] = useState(false);
  const [returnIdOpen, setReturnIdOpen] = useState(false);
  const selectedUserIds = filters.user_ids?.length ? filters.user_ids : filters.user_id ? [filters.user_id] : [];
  const selectedProviderIds = filters.provider_ids?.length ? filters.provider_ids : filters.provider_id ? [filters.provider_id] : [];

  // Count how many non-default filters are active
  const activeCount = [
    filters.status,
    filters.delivery_status,
    filters.district,
    selectedUserIds.length || selectedProviderIds.length,
    filters.driver_ids?.length,
    filters.date_from,
    filters.date_to,
    filters.delivery_date,
    filters.fulfillment_state,
    filters.product_id,
    filters.item_quantity,
    filters.product_stock,
  ].filter(Boolean).length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* Filter toggle header */}
      <div className="flex min-w-0 items-stretch">
        <button
          type="button"
          onClick={() => { if (!expanded) { requestUsers(); requestDrivers(); } setExpanded(prev => !prev); }}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-l-xl px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <Filter size={16} className="text-gray-500" />
          <span className="font-montserrat font-semibold text-sm text-gray-700 flex-1">
            Filtros
          </span>
          {activeCount > 0 && (
            <span className="bg-brand-primary text-white text-xs font-montserrat font-bold px-2 py-0.5 rounded-full">
              {activeCount}
            </span>
          )}
          <ChevronDown
            size={16}
            className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        {onOpenProductCounts && <button type="button" aria-label="Ver conteo de productos" title="Ver productos de todos los paquetes filtrados"
          onClick={onOpenProductCounts} className="flex items-center gap-1.5 border-l border-gray-100 px-3 text-sm font-semibold text-brand-primary hover:bg-gray-50">
          <Boxes size={17}/><span className="hidden sm:inline">Productos</span>
          <span>{productCountsLoading ? '…' : productCounts ? `${productCounts.total_units} unid.` : '—'}</span>
        </button>}
        {onAddReturnPackage && <button type="button" aria-label="Agregar paquete por ID de entrega" title="Agregar paquete por ID de entrega"
          aria-expanded={returnIdOpen} onClick={() => setReturnIdOpen(prev => !prev)}
          className="relative flex min-w-12 items-center justify-center rounded-r-xl border-l border-gray-100 px-3 text-brand-primary hover:bg-gray-50">
          <Hash size={20} />
          {returnDraft.length > 0 && <span className="absolute right-0.5 top-0.5 rounded-full bg-brand-primary px-1 text-[10px] font-bold text-white">{returnDraft.length}</span>}
        </button>}
      </div>

      {returnIdOpen && onAddReturnPackage && <ReturnIdPicker draft={returnDraft} onAdd={onAddReturnPackage}
        onRemove={onRemoveReturnPackage} onPrepare={onPrepareReturnDraft} />}

      {/* Filter fields */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4">
          <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">

            {/* User selector */}
            <div className="relative z-20 min-w-0 space-y-1 md:col-span-2 xl:col-span-2">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Tiendas y proveedores
              </label>
              <UserSelector
                onSearch={onUserSearch}
                users={users}
                usersLoading={usersLoading}
                activeUsersOnly={activeUsersOnly}
                setActiveUsersOnly={setActiveUsersOnly}
                selectedUserIds={selectedUserIds}
                selectedProviderIds={selectedProviderIds}
                onChange={({ user_ids, provider_ids }) => applyFilter({
                  user_ids, provider_ids,
                  user_id: user_ids.length === 1 && !provider_ids.length ? user_ids[0] : '',
                  provider_id: provider_ids.length === 1 && !user_ids.length ? provider_ids[0] : '',
                  product_id: '', item_quantity: '', product_stock: '',
                })}
              />
            </div>

            {(filters.user_id || filters.provider_id) && <>
              <div className="relative z-10 min-w-0 space-y-1">
                <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">Producto</label>
                <ProductSelector key={`${filters.user_id}:${filters.provider_id}`}
                  userId={filters.user_id} providerId={filters.provider_id} selectedId={filters.product_id}
                  onChange={product_id => applyFilter({ product_id, product_stock: '' })}/>
              </div>
              <div className="min-w-0 space-y-1">
                <label htmlFor="package-item-quantity" className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">Cantidad del producto</label>
                <input id="package-item-quantity" type="number" min="1" step="1" inputMode="numeric"
                  value={filters.item_quantity || ''}
                  onChange={event => { const value = event.target.value; if (!value || /^[1-9]\d*$/.test(value)) applyFilter({ item_quantity: value }); }}
                  placeholder="Cualquier cantidad"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none"/>
              </div>
              <div className="min-w-0 space-y-1">
                <label htmlFor="package-product-stock" className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">Stock Fast Max</label>
                <select id="package-product-stock" value={filters.product_stock || ''} disabled={!filters.product_id}
                  onChange={event => applyFilter({ product_stock: event.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="">Todo el stock</option>
                  <option value="sufficient">Suficiente para esta línea</option>
                  <option value="insufficient">Insuficiente para esta línea</option>
                </select>
                {filters.product_id && <p className="text-xs text-gray-500">Evalúa esta línea e incluye su reserva. Otros productos pueden necesitar stock. Saldos sin conciliar son referenciales.</p>}
              </div>
            </>}

            {/* Assigned delivery driver(s) */}
            <div className="relative z-10 min-w-0 space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Conductor
              </label>
              <DriverSelector
                drivers={drivers}
                loading={driversLoading}
                error={driversError}
                selectedIds={filters.driver_ids}
                onChange={driver_ids => applyFilter({ driver_ids })}
              />
            </div>

            {/* Package status select */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Estado paquete
              </label>
              <select
                value={filters.status}
                onChange={e => applyFilter({ status: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-primary"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Delivery status select */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Estado entrega
              </label>
              <select
                value={filters.delivery_status}
                onChange={e => applyFilter({ delivery_status: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-primary"
              >
                {DELIVERY_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* District input */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Distrito
              </label>
              <input
                type="text"
                value={filters.district}
                onChange={e => applyFilter({ district: e.target.value })}
                placeholder="Ej: Miraflores"
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Date from */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Desde
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={e => applyFilter({ date_from: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Date to */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Hasta
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={e => applyFilter({ date_to: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Delivery date */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Fecha entrega
              </label>
              <input
                type="date"
                value={filters.delivery_date}
                onChange={e => applyFilter({ delivery_date: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Clear filters */}
          {activeCount > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-sm font-montserrat font-semibold text-red-500 hover:text-red-700 transition-colors"
              >
                <X size={14} />
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
